import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ALL_EXPEDIENT_TYPES,
  EXPEDIENT_TYPE_LABELS,
} from '@claimup/shared/constants/expedient';
import api, { getErrorMessage } from '../../../services/api';

/**
 * KPIs del canal de llamadas.
 *
 * La columna que manda es **Conversaciones**, no **Descolgadas**: una
 * conversación útil es una descolgada de **≥45 s**, y está medido contra
 * producción — por debajo de ese umbral los expedientes pagan igual que los que
 * nunca descolgaron. Enseñar solo descolgadas infla el número entre tres y cuatro
 * veces y hace creer que el canal va mejor de lo que va.
 *
 * Por eso las dos van juntas y la **tasa de contacto se calcula sobre las
 * útiles**. Es un número más feo y es el que sirve para decidir.
 */

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const pct = (v) => `${Math.round((v || 0) * 1000) / 10}%`;
const mmss = (s) => {
  const total = Math.max(0, Math.round(s || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Las líneas de negocio, para filtrar y para agrupar.
 *
 * Importa más de lo que parece: el marcador **nunca fue solo de AR** —los tipos
 * de tarea `follow_up_call` y `reply_missed_call` son de recobro—, así que sin
 * este filtro la tabla suma las dos líneas y no se puede comparar Telnyx contra
 * Aircall, que es de recobro.
 *
 * Sale de las constantes compartidas y no de una lista escrita aquí: una línea
 * nueva aparecería sola, y las etiquetas no pueden decir una cosa en esta
 * pantalla y otra en el resto de la aplicación.
 */
const LINES = [
  { value: '', label: 'Todas las líneas' },
  ...ALL_EXPEDIENT_TYPES.map((value) => ({ value, label: EXPEDIENT_TYPE_LABELS[value] || value })),
];

/**
 * El encabezado de la primera columna, por agrupación.
 *
 * Era un ternario `by === 'company' ? 'Cliente' : 'Agente'`, que ya mentía con
 * `by: 'line'` —ponía «Agente» sobre una columna de líneas de negocio—. Con una
 * agrupación más el ternario no da para todas, y una tabla cuya cabecera no dice
 * lo que hay debajo se lee mal sin que nadie sospeche del código.
 */
const COLUMN_LABELS = {
  company: 'Cliente',
  gestor: 'Agente',
  line: 'Línea de negocio',
  phone: 'Número',
};

const CARDS = [
  { key: 'useful', label: 'Conversaciones', hint: 'descolgadas de 45 s o más' },
  { key: 'dials', label: 'Marcaciones', hint: 'métrica de la máquina, no del gestor' },
  { key: 'missed', label: 'No contestan', hint: 'nadie descolgó' },
  { key: 'voicemail', label: 'Buzones', hint: 'cortados por el AMD' },
  { key: 'promises', label: 'Promesas de pago', hint: 'promesa o plan cerrado' },
];

const CallKpis = () => {
  const [range, setRange] = useState({ from: daysAgo(30), to: today() });
  const [by, setBy] = useState('company');
  const [line, setLine] = useState('');
  const [phone, setPhone] = useState('');
  const [phones, setPhones] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Los números del desplegable salen del POOL (`/calls/phones`), no de las
   * marcaciones.
   *
   * Es a propósito: por el filtro de dirección se cuelan entrantes mal
   * etiquetadas cuyo `from_number` es el móvil del DEUDOR. Sacando el
   * desplegable de las marcaciones, el gestor podría filtrar por el móvil de un
   * deudor creyendo que es una línea nuestra. El pool es la lista autoritativa
   * de lo que es nuestro.
   *
   * Un fallo aquí no rompe la pantalla: se queda sin el filtro y los KPIs se ven
   * igual, que es lo que de verdad se viene a mirar.
   */
  useEffect(() => {
    let vivo = true;
    api
      .get('/calls/phones')
      .then(({ data }) => {
        if (vivo) setPhones(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `line` vacío se omite: mandar `line=` filtraría por cadena vacía y la
      // tabla saldría a cero sin decir por qué.
      const params = {
        from: range.from,
        to: range.to,
        ...(line ? { line } : {}),
        ...(phone ? { fromNumber: phone } : {}),
      };
      const [s, b] = await Promise.all([
        api.get('/calls/stats/summary', { params }),
        api.get('/calls/stats/breakdown', { params: { ...params, by } }),
      ]);
      setSummary(s.data?.data || null);
      setRows(Array.isArray(b.data?.data) ? b.data.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, by, line, phone]);

  useEffect(() => {
    load();
  }, [load]);

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows]);

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4'>
        <label className='text-sm text-slate-600'>
          Desde
          <input
            type='date'
            value={range.from}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
            className='ml-2 rounded-lg border border-slate-300 px-2 py-1 text-sm'
          />
        </label>
        <label className='text-sm text-slate-600'>
          Hasta
          <input
            type='date'
            value={range.to}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
            className='ml-2 rounded-lg border border-slate-300 px-2 py-1 text-sm'
          />
        </label>

        <label className='text-sm text-slate-600'>
          Línea
          <select
            value={line}
            onChange={(e) => setLine(e.target.value)}
            className='ml-2 rounded-lg border border-slate-300 px-2 py-1 text-sm'
          >
            {LINES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {/*
          El número con el que SALIÓ la llamada: la identidad que oye el deudor.
          Es lo que permite comparar un CLI móvil contra los geográficos sin que
          las dos identidades se sumen en la misma tasa de contacto.
        */}
        <label className='text-sm text-slate-600'>
          Número
          <select
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className='ml-2 rounded-lg border border-slate-300 px-2 py-1 text-sm'
          >
            <option value=''>Todos los números</option>
            {phones.map((p) => (
              <option key={p._id || p.phone_number} value={p.phone_number}>
                {p.nombre ? `${p.phone_number} · ${p.nombre}` : p.phone_number}
              </option>
            ))}
          </select>
        </label>

        {/*
          Un solo desplegable para los dos tipos de agente: gestores de ClaimUp y
          operadores dentro del cliente son ambos `users`, y lo que queda grabado
          en la marcación es su id. Separarlos en dos filtros sería inventar una
          distinción que los datos no tienen.
        */}
        <label className='text-sm text-slate-600'>
          Agrupar por
          <select
            value={by}
            onChange={(e) => setBy(e.target.value)}
            className='ml-2 rounded-lg border border-slate-300 px-2 py-1 text-sm'
          >
            <option value='company'>Cliente</option>
            <option value='gestor'>Agente</option>
            <option value='line'>Línea de negocio</option>
            <option value='phone'>Número</option>
          </select>
        </label>
      </div>

      <div className='grid grid-cols-2 gap-3 lg:grid-cols-5'>
        {CARDS.map(({ key, label, hint }) => (
          <div key={key} className='rounded-lg border border-slate-200 bg-white p-4'>
            <p className='text-2xl font-semibold text-slate-900'>{summary ? summary[key] ?? 0 : '—'}</p>
            <p className='text-sm font-medium text-slate-700'>{label}</p>
            <p className='mt-0.5 text-xs text-slate-400'>{hint}</p>
          </div>
        ))}
      </div>

      {summary && (
        <div className='flex flex-wrap gap-6 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm'>
          <span className='text-slate-600'>
            Tasa de contacto útil <strong className='text-slate-900'>{pct(summary.contact_rate)}</strong>
          </span>
          <span className='text-slate-400'>
            (descolgadas sin filtrar: {pct(summary.answer_rate)})
          </span>
          <span className='text-slate-600'>
            Duración media <strong className='text-slate-900'>{mmss(summary.aht_seconds)}</strong>
          </span>
          {summary.abandoned > 0 && (
            <span className='font-medium text-amber-700'>{summary.abandoned} abandonadas</span>
          )}
        </div>
      )}

      <div className='overflow-hidden rounded-lg border border-slate-200 bg-white'>
        <div className='overflow-x-auto'>
          <table className='min-w-full text-sm'>
            <thead className='bg-slate-50 text-left text-slate-600'>
              <tr>
                <th className='px-4 py-3 font-medium'>{COLUMN_LABELS[by] || COLUMN_LABELS.company}</th>
                <th className='px-4 py-3 text-right font-medium'>Conversaciones</th>
                <th className='px-4 py-3 text-right font-medium'>Marcaciones</th>
                <th className='px-4 py-3 text-right font-medium'>Contacto</th>
                <th className='px-4 py-3 text-right font-medium'>No contestan</th>
                <th className='px-4 py-3 text-right font-medium'>Buzones</th>
                <th className='px-4 py-3 text-right font-medium'>Duración media</th>
                <th className='px-4 py-3 text-right font-medium'>Promesas</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {rows.map((row) => (
                <tr key={row.id || 'sin-asignar'} className='hover:bg-slate-50'>
                  <td className='px-4 py-3 text-slate-900'>{row.name}</td>
                  <td className='px-4 py-3 text-right font-medium text-slate-900'>{row.useful}</td>
                  <td className='px-4 py-3 text-right text-slate-500'>{row.dials}</td>
                  <td className='px-4 py-3 text-right text-slate-700'>{pct(row.contact_rate)}</td>
                  <td className='px-4 py-3 text-right text-slate-500'>{row.missed}</td>
                  <td className='px-4 py-3 text-right text-slate-500'>{row.voicemail}</td>
                  <td className='px-4 py-3 text-right text-slate-700'>{mmss(row.aht_seconds)}</td>
                  <td className='px-4 py-3 text-right text-slate-900'>{row.promises}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <p className='px-4 py-6 text-sm text-slate-400'>Cargando…</p>}
        {empty && <p className='px-4 py-6 text-sm text-slate-400'>No hay llamadas en este periodo.</p>}
      </div>
    </div>
  );
};

export default CallKpis;
